import { ArrowUpRight } from "lucide-react";

export function Mark7({ size = 40, className = "" }) {
  const inner = Math.round(size * 0.72);
  return (
    <span className={`inline-flex items-center justify-center bg-ink shrink-0 ${className}`} style={{ width: size, height: size }} aria-hidden="true">
      <span className="flex items-center justify-center bg-white border border-gold" style={{ width: inner, height: inner }}>
        <span className="f-display font-semibold text-red7 leading-none" style={{ fontSize: inner * 0.82, marginTop: inner * 0.06 }}>7</span>
      </span>
    </span>
  );
}

export function Crown({ className = "" }) {
  return (
    <svg viewBox="0 0 24 14" className={className} aria-hidden="true">
      <path d="M2 12 L1 3 L6.5 7.5 L9 1.5 L12 6.5 L15 1.5 L17.5 7.5 L23 3 L22 12 Z" fill="#D9A520" />
      <rect x="2" y="11.5" width="20" height="2" fill="#B8860B" />
      <circle cx="12" cy="9.2" r="1.4" fill="#A32937" />
    </svg>
  );
}

export function Wordmark({ dark = false, size = "md" }) {
  const big = size === "lg";
  return (
    <span className="inline-flex flex-col leading-none" aria-label="Se7en Capital Group">
      <span className={`f-display font-semibold ${big ? "text-3xl tracking-[0.22em]" : "text-lg sm:text-xl tracking-[0.2em]"} ${dark ? "text-white" : "text-ink"}`}>
        SE
        <span className={`relative inline-block ${dark ? "text-red7b" : "text-red7"}`}>
          7
          <Crown className={`absolute left-1/2 -translate-x-1/2 ${big ? "-top-3.5 w-5" : "-top-2.5 w-3.5"}`} />
        </span>
        EN
      </span>
      <span className={`f-mono ${big ? "text-[10px] mt-1.5" : "text-[8px] mt-1"} tracking-[0.32em] ${dark ? "text-gold" : "text-goldtext"}`}>CAPITAL GROUP</span>
    </span>
  );
}

export function Ornament({ className = "" }) {
  return (
    <div className={`flex items-center justify-center gap-3 ${className}`} aria-hidden="true">
      <span className="h-px w-16 sm:w-24 bg-gold" />
      <span className="w-2 h-2 rotate-45 bg-red7" />
      <span className="h-px w-16 sm:w-24 bg-gold" />
    </div>
  );
}

export const TAGLINE = "BUILDING BETTER CREDIT · FUNDING YOUR FUTURE · CREATING FINANCIAL FREEDOM";
export function Tagline({ className = "" }) {
  return <p className={`f-mono text-[10px] sm:text-[11px] tracking-[0.2em] text-goldtext text-center ${className}`}>{TAGLINE}</p>;
}

export function Eyebrow({ n, children, light = false, className = "" }) {
  return (
    <p className={`eyebrow ${light ? "text-gold" : "text-red7"} ${className}`}>
      {n ? `${n} — ` : ""}{children}
    </p>
  );
}

export function Seal({ dark = false, text = "FASTEST ◆ MOST AFFORDABLE ◆ SE7EN ◆ ", size = 112 }) {
  const id = dark ? "sealpath-d" : "sealpath-l";
  return (
    <div className="relative" style={{ width: size, height: size }} aria-hidden="true">
      <svg viewBox="0 0 120 120" className="absolute inset-0 seal-spin">
        <defs><path id={id} d="M60,60 m-44,0 a44,44 0 1,1 88,0 a44,44 0 1,1 -88,0" /></defs>
        <circle cx="60" cy="60" r="58" fill={dark ? "#111" : "#fff"} stroke="#D9A520" strokeWidth="1" />
        <text fontFamily="JetBrains Mono, monospace" fontSize="9.4" letterSpacing="2.2" fill={dark ? "#D9A520" : "#111"} fontWeight="600">
          <textPath href={`#${id}`}>{text}</textPath>
        </text>
      </svg>
      <span className={`absolute inset-0 flex items-center justify-center f-display font-semibold ${dark ? "text-red7b" : "text-red7"}`} style={{ fontSize: size * 0.34 }}>7</span>
    </div>
  );
}

export function LiveDot({ label = "Seven is online", light = false, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-2 f-mono text-[10px] tracking-[0.16em] uppercase ${light ? "text-white/80" : "text-muted7"} ${className}`} data-testid="seven-online">
      <span className="pulse-dot" />{label}
    </span>
  );
}

export function Btn({ as = "button", variant = "black", arrow = false, className = "", children, ...p }) {
  const Comp = as;
  return (
    <Comp className={`btn btn-${variant} ${className}`} {...p}>
      {children}
      {arrow && <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />}
    </Comp>
  );
}

export function Chip({ tone = "line", children, className = "", ...p }) {
  return <span className={`chip chip-${tone} ${className}`} {...p}>{children}</span>;
}

export function Diamond({ className = "" }) {
  return <span className={`inline-block w-1.5 h-1.5 rotate-45 bg-red7 align-middle ${className}`} aria-hidden="true" />;
}

export function Empty({ title, text, testid }) {
  return (
    <div className="border border-dashed border-tanline bg-cream p-8 text-center" data-testid={testid}>
      <p className="f-display text-xl">{title}</p>
      {text && <p className="text-sm text-muted7 mt-2">{text}</p>}
    </div>
  );
}

export function Skeleton({ className = "h-24" }) {
  return <div className={`skeleton ${className}`} />;
}
